import { translate } from 'utils/i18nTranslate';
import { ROUTES } from 'utils/constants';
import {
  COMPANY_SETTINGS_PERMISSION_IDS,
  EMPLOYEE_PORTAL_MODAL_PERMISSION_IDS,
  HRIS_MODAL_PERMISSION_IDS,
  JOURNALS_MODAL_PERMISSION_IDS,
  LEGACY_COMPANY_SETTINGS_PERMISSION_ID,
  LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID,
  LEGACY_JOURNAL_PERMISSION_IDS,
  OWNER_ONLY_PERMISSION_IDS,
  permissionIds,
  type CompanySettingsPermissionId,
  type JournalsModalPermissionId,
} from './permissionIds';

export {
  COMPANY_SETTINGS_PERMISSION_IDS,
  EMPLOYEE_PORTAL_MODAL_PERMISSION_IDS,
  HRIS_MODAL_PERMISSION_IDS,
  JOURNALS_MODAL_PERMISSION_IDS,
  LEGACY_COMPANY_SETTINGS_PERMISSION_ID,
  LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID,
  LEGACY_JOURNAL_PERMISSION_IDS,
  permissionIds,
  type CompanySettingsPermissionId,
  type EmployeePortalModalPermissionId,
  type JournalsModalPermissionId,
  type PermissionId,
} from './permissionIds';

export interface AppPermission {
  id: string;
  endpoint?: string;
  page?: string;
}

/** Modal layout mirrors main nav + in-page sections (journals, reports, settings sidebars). */
export type PermissionModalBlock = {
  /** Top-level group — matches main sidebar (Journals, Reports, Settings). */
  group:
    | 'journals'
    | 'reports'
    | 'settings'
    | 'dashboard'
    | 'hris'
    | 'employeePortal'
    | 'pos'
    | 'shop'
    | 'instoreqr'
    | 'events'
    | 'reserve'
    | 'fileManager'
    | 'healthCheck'
    | 'taxCalendar';
  /** In-page subsection title when the app splits items (e.g. Reports page groups). */
  subgroup?:
    | 'financialStatements'
    | 'journals'
    | 'companySettings'
    | 'companyManagement'
    | 'system';
  permissionIds: readonly string[];
};

function translatePermissionName(permissionId: string): string {
  return translate(`utils.appPermissions.permissions.${permissionId}`);
}

function translatePermissionGroup(
  group: PermissionModalBlock['group'],
): string {
  return translate(`utils.appPermissions.groups.${group}`);
}

function translatePermissionSubgroup(
  subgroup: NonNullable<PermissionModalBlock['subgroup']>,
): string {
  return translate(`utils.appPermissions.subgroups.${subgroup}`);
}

export const PERMISSION_MODAL_BLOCKS: PermissionModalBlock[] = [
  {
    group: 'journals',
    permissionIds: [
      ...JOURNALS_MODAL_PERMISSION_IDS,
      permissionIds.uploadEntry,
      permissionIds.uploadEntryUploadOnly,
    ],
  },
  {
    group: 'dashboard',
    permissionIds: [
      permissionIds.dashboardTaxReminders,
      permissionIds.dashboardTotalIncome,
      permissionIds.dashboardTotalExpenses,
      permissionIds.dashboardTotalProfit,
      permissionIds.dashboardRevenueExpensesChart,
      permissionIds.dashboardPnlSummary,
      permissionIds.dashboardRecentTransactions,
      permissionIds.dashboardExpenseBreakdown,
    ],
  },
  {
    group: 'reports',
    subgroup: 'financialStatements',
    permissionIds: [
      permissionIds.reportsBalanceSheet,
      permissionIds.reportsIncomeStatement,
      permissionIds.reportsTrialBalance,
      permissionIds.reportsEquity,
      permissionIds.reportsCashFlow,
      permissionIds.reportsFs,
    ],
  },
  {
    group: 'reports',
    subgroup: 'journals',
    permissionIds: [
      permissionIds.reportsCashReceiptsJournal,
      permissionIds.reportsCashDisbursementJournal,
      permissionIds.reportsGeneralJournal,
      permissionIds.reportsLedger,
      permissionIds.withholdingTaxReports,
    ],
  },
  {
    group: 'pos',
    permissionIds: [permissionIds.posSystem],
  },
  {
    group: 'shop',
    permissionIds: [permissionIds.shop],
  },
  {
    group: 'instoreqr',
    permissionIds: [permissionIds.instoreqr],
  },
  {
    group: 'events',
    permissionIds: [permissionIds.eventsManagement],
  },
  {
    group: 'reserve',
    permissionIds: [permissionIds.reserve],
  },
  {
    group: 'fileManager',
    permissionIds: [permissionIds.fileManager],
  },
  {
    group: 'healthCheck',
    permissionIds: [permissionIds.healthCheck],
  },
  {
    group: 'taxCalendar',
    permissionIds: [
      permissionIds.taxCalendar,
      permissionIds.taxCalendarViewOnly,
    ],
  },
  {
    group: 'settings',
    subgroup: 'companySettings',
    permissionIds: [
      ...COMPANY_SETTINGS_PERMISSION_IDS,
      permissionIds.businessCalendar,
    ],
  },
  {
    group: 'settings',
    subgroup: 'companyManagement',
    permissionIds: [
      permissionIds.viewCompanies,
      permissionIds.editCompany,
      permissionIds.apiKeys,
    ],
  },
  {
    group: 'settings',
    subgroup: 'system',
    permissionIds: [permissionIds.activityLogs, permissionIds.appsStore],
  },
  {
    group: 'hris',
    permissionIds: [...HRIS_MODAL_PERMISSION_IDS],
  },
  {
    group: 'employeePortal',
    permissionIds: [...EMPLOYEE_PORTAL_MODAL_PERMISSION_IDS],
  },
];

export type PermissionModalGroup = {
  group: string;
  subsections: Array<{
    subgroup?: string;
    permissions: Array<AppPermission & { name: string }>;
  }>;
};

export const APP_PERMISSIONS: AppPermission[] = [
  {
    id: permissionIds.recordIncome,
    endpoint: '/sales',
  },
  {
    id: permissionIds.recordExpenses,
    endpoint: '/expenses',
  },
  {
    id: permissionIds.billerManagement,
    endpoint: '/accounts-payable/bills',
    page: '/accounts-payable/bills',
  },
  {
    id: permissionIds.arInvoices,
    endpoint: '/ar-invoices',
    page: '/accounts-receivable/invoices',
  },
  {
    id: permissionIds.equipmentManagement,
    endpoint: '/equipment',
    page: '/ppe',
  },
  {
    id: permissionIds.capitalManagement,
    endpoint: '/capitals',
    page: '/capitals',
  },
  {
    id: permissionIds.recordCustomTransactions,
    endpoint: '/transactions',
  },
  {
    id: permissionIds.categories,
    endpoint: '/categories',
  },
  {
    id: permissionIds.customers,
    endpoint: '/customers',
    page: '/journals/customers',
  },
  {
    id: permissionIds.sendCustomerEmails,
    endpoint: '/customers/send-emails',
    page: '/journals/customers/email',
  },
  {
    id: permissionIds.billInvoiceSettings,
    endpoint: '/bill-invoice-settings',
    page: '/journals/customers/bill-invoice',
  },
  {
    id: permissionIds.suppliers,
    endpoint: '/suppliers',
    page: '/journals/suppliers',
  },
  {
    id: permissionIds.uploadEntry,
    endpoint: '/upload-entries',
    page: '/upload-entry',
  },
  {
    id: permissionIds.uploadEntryUploadOnly,
    endpoint: '/upload-entries',
    page: '/upload-entry',
  },
  {
    id: permissionIds.taxCalendar,
    page: '/tax-compliance',
  },
  {
    id: permissionIds.taxCalendarViewOnly,
    page: '/tax-compliance',
  },
  {
    id: permissionIds.withholdingTaxReports,
    endpoint: '/cash-disbursements-journal',
    page: '/tax-compliance/qap',
  },
  {
    id: permissionIds.businessCalendar,
    page: '/business-calendar',
  },
  {
    id: permissionIds.eventsManagement,
    endpoint: '/events',
    page: '/events',
  },
  {
    id: permissionIds.reserve,
    endpoint: '/reserve',
    page: '/reserve',
  },
  {
    id: permissionIds.fileManager,
    endpoint: '/file-manager',
    page: '/file-manager',
  },
  {
    id: permissionIds.dashboardTaxReminders,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardTotalIncome,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardTotalExpenses,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardTotalProfit,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardRevenueExpensesChart,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardPnlSummary,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardRecentTransactions,
    page: '/dashboard',
  },
  {
    id: permissionIds.dashboardExpenseBreakdown,
    page: '/dashboard',
  },
  {
    id: permissionIds.reportsIncomeStatement,
    endpoint: '/reports/income-statement',
  },
  {
    id: permissionIds.reportsTrialBalance,
    endpoint: '/reports/trial-balance',
  },
  {
    id: permissionIds.reportsBalanceSheet,
    endpoint: '/reports/balance-sheet',
  },
  {
    id: permissionIds.reportsCashFlow,
    endpoint: '/reports/cash-flow-statement',
  },
  {
    id: permissionIds.reportsEquity,
    endpoint: '/reports/statement-of-changes-in-equity',
  },
  {
    id: permissionIds.reportsFs,
    endpoint: '/reports/financial-statement',
  },
  {
    id: permissionIds.reportsCashReceiptsJournal,
    endpoint: '/reports/cash-receipts-journal',
  },
  {
    id: permissionIds.reportsCashReceiptsJournal,
    endpoint: '/crj-ledger',
  },
  {
    id: permissionIds.reportsCashDisbursementJournal,
    endpoint: '/reports/cash-disbursement-journal',
  },
  {
    id: permissionIds.reportsCashDisbursementJournal,
    endpoint: '/cdj-ledger',
  },
  {
    id: permissionIds.reportsLedger,
    endpoint: '/reports/general-ledger',
  },
  {
    id: permissionIds.reportsGeneralJournal,
    endpoint: '/reports/general-journal',
  },
  {
    id: permissionIds.posSystem,
    endpoint: '/pos',
  },
  {
    id: permissionIds.shop,
    page: '/shop',
  },
  {
    id: permissionIds.instoreqr,
    page: '/instoreqr',
    endpoint: '/instoreqr',
  },
  {
    id: permissionIds.settingsPeriodLocks,
    endpoint: '/settings/transaction-period-locks',
  },
  {
    id: permissionIds.settingsCurrency,
    page: '/settings/company-settings',
  },
  {
    id: permissionIds.settingsCategoryAccountNumbers,
    page: '/settings/company-settings',
  },
  {
    id: permissionIds.viewCompanies,
    page: '/settings/companies',
  },
  {
    id: permissionIds.editCompany,
    page: '/settings/companies',
  },
  {
    id: permissionIds.editCompany,
    page: '/settings/bir-taxpayer-profile',
  },
  {
    id: permissionIds.apiKeys,
    endpoint: '/api-keys',
    page: '/api-keys',
  },
  {
    id: permissionIds.activityLogs,
    endpoint: '/activity-logs',
    page: '/activity-logs',
  },
  {
    id: permissionIds.appsStore,
    // List installs (GET /company-apps) is open to all members; only the
    // Apps Store SPA and install/uninstall mutations use this permission.
    page: '/apps-store',
  },
  {
    id: permissionIds.healthCheck,
    endpoint: '/company-health-check',
    page: '/health-check',
  },
  {
    id: permissionIds.hris,
    endpoint: '/hris',
    page: '/hris',
  },
  {
    id: permissionIds.hrisEmployees,
    endpoint: '/hris/employees',
    page: '/hris/employees',
  },
  {
    id: permissionIds.hrisAttendance,
    endpoint: '/hris/attendance',
    page: '/hris/attendance',
  },
  {
    id: permissionIds.hrisLeave,
    endpoint: '/hris/leave',
    page: '/hris/leave',
  },
  {
    id: permissionIds.hrisPayroll,
    endpoint: '/hris/payroll',
    page: '/hris/payroll',
  },
  {
    id: permissionIds.hrisSettings,
    endpoint: '/hris/settings',
    page: '/hris/settings',
  },
  {
    id: permissionIds.hrisReports,
    endpoint: '/hris/reports',
    page: '/hris/reports',
  },
  {
    id: permissionIds.employeePortal,
    page: '/portal',
  },
  {
    id: permissionIds.employeePortalAttendance,
    page: '/portal/attendance',
  },
];

const PERMISSION_BY_ID = new Map(
  APP_PERMISSIONS.map((permission) => [permission.id, permission]),
);

/** Groups and sorts assignable permissions for the Manage User Permissions modal. */
export function buildPermissionModalGroups(
  searchQuery = '',
): PermissionModalGroup[] {
  const query = searchQuery.trim().toLowerCase();
  const groups: PermissionModalGroup[] = [];

  for (const block of PERMISSION_MODAL_BLOCKS) {
    const groupLabel = translatePermissionGroup(block.group);
    const subgroupLabel = block.subgroup
      ? translatePermissionSubgroup(block.subgroup)
      : undefined;

    const permissions = block.permissionIds
      .map((id) => PERMISSION_BY_ID.get(id))
      .filter((permission): permission is AppPermission => permission != null)
      .map((permission) => ({
        ...permission,
        name: translatePermissionName(permission.id),
      }))
      .filter((permission) => {
        if (!query) {
          return true;
        }
        const pathStr = permission.endpoint || permission.page || '';
        return (
          permission.name.toLowerCase().includes(query) ||
          pathStr.toLowerCase().includes(query) ||
          groupLabel.toLowerCase().includes(query) ||
          subgroupLabel?.toLowerCase().includes(query)
        );
      });

    if (permissions.length === 0) {
      continue;
    }

    const last = groups[groups.length - 1];
    if (last?.group === groupLabel) {
      last.subsections.push({
        subgroup: subgroupLabel,
        permissions,
      });
      continue;
    }

    groups.push({
      group: groupLabel,
      subsections: [{ subgroup: subgroupLabel, permissions }],
    });
  }

  return groups;
}

/** Paths that are always available to authenticated users (not gated by company member permissions). */
const USER_LEVEL_PATH_PREFIXES = [
  '/dashboard',
  '/super-dashboard',
  '/super-dashboard/tax-reminders',
  '/super-dashboard/upload-entries',
  '/help',
  '/support',
  '/legal',
  '/superadmin',
  '/settings/general',
  '/settings/notifications',
  '/settings/profile',
  '/settings/change-password',
  '/settings/companies',
  '/settings/customer-care',
  '/companies',
  '/super-admin',
  '/user/profile',
  '/user/change-password',
  '/user/onboarding',
  '/company/invite',
  '/invite',
  '/notifications',
] as const;

export function isUserLevelPath(path: string): boolean {
  const clean = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return USER_LEVEL_PATH_PREFIXES.some(
    (prefix) => clean === prefix || clean.startsWith(`${prefix}/`),
  );
}

export function memberHasAnyEndpointPermission(
  permissionIdsList: string[],
  endpointPrefixes: string[],
): boolean {
  return APP_PERMISSIONS.some(
    (permission) =>
      permissionIdsList.includes(permission.id) &&
      endpointPrefixes.some((prefix) =>
        permission.endpoint?.startsWith(prefix),
      ),
  );
}

function isPathMatched(path: string, endpoint: string): boolean {
  const cleanPath = path.split('?')[0].split('#')[0].replace(/\/$/, '');
  const cleanEndpoint = endpoint.replace(/\/$/, '');

  if (cleanEndpoint.includes('/:')) {
    const pathParts = cleanPath.split('/');
    const endpointParts = cleanEndpoint.split('/');
    if (pathParts.length !== endpointParts.length) return false;
    return endpointParts.every(
      (part, i) => part.startsWith(':') || part === pathParts[i],
    );
  }

  return (
    cleanPath === cleanEndpoint || cleanPath.startsWith(cleanEndpoint + '/')
  );
}

export function getPagePermissionId(path: string): string | null {
  if (isUserLevelPath(path)) {
    return null;
  }
  for (const entry of APP_PERMISSIONS) {
    if (entry.endpoint && isPathMatched(path, entry.endpoint)) {
      return entry.id;
    }
    if (entry.page && isPathMatched(path, entry.page)) {
      return entry.id;
    }
  }
  return null;
}

export function memberHasCompanySettingsPermission(
  permissionIdsList: string[],
  permissionId: CompanySettingsPermissionId,
): boolean {
  if (permissionIdsList.includes(LEGACY_COMPANY_SETTINGS_PERMISSION_ID)) {
    return true;
  }
  return permissionIdsList.includes(permissionId);
}

export function memberHasCompanySettingsPageAccess(
  permissionIdsList: string[],
): boolean {
  if (permissionIdsList.includes(LEGACY_COMPANY_SETTINGS_PERMISSION_ID)) {
    return true;
  }
  return COMPANY_SETTINGS_PERMISSION_IDS.some((id) =>
    permissionIdsList.includes(id),
  );
}

/** Header company info and Settings > Company Information. */
export function memberHasCompanyInfoAccess(
  permissionIdsList: string[],
): boolean {
  return (
    permissionIdsList.includes(permissionIds.viewCompanies) ||
    permissionIdsList.includes(permissionIds.editCompany)
  );
}

/** Company paths reserved for the company owner (not assignable to members). */
export const OWNER_ONLY_COMPANY_PATHS = [
  '/settings/users',
  '/settings/billing',
] as const;

export function isOwnerOnlyCompanyPath(path: string): boolean {
  const cleanPath = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return OWNER_ONLY_COMPANY_PATHS.some(
    (ownerPath) =>
      cleanPath === ownerPath || cleanPath.startsWith(`${ownerPath}/`),
  );
}

const LEGACY_JOURNAL_PERMISSION_ALIASES: Record<
  JournalsModalPermissionId,
  readonly string[]
> = {
  [permissionIds.recordIncome]: [permissionIds.legacyCashReceiptsJournal],
  [permissionIds.recordExpenses]: [],
  [permissionIds.billerManagement]: [permissionIds.recordExpenses],
  [permissionIds.arInvoices]: [],
  [permissionIds.equipmentManagement]: [permissionIds.recordExpenses],
  [permissionIds.capitalManagement]: [permissionIds.recordCustomTransactions],
  [permissionIds.recordCustomTransactions]: [
    permissionIds.legacyTransactionsAdd,
    permissionIds.legacyTransactionsView,
    permissionIds.legacyTransactionsEdit,
    permissionIds.legacyTransactionsReceipt,
  ],
  [permissionIds.categories]: [permissionIds.arInvoices],
  [permissionIds.customers]: [
    permissionIds.recordIncome,
    permissionIds.arInvoices,
  ],
  [permissionIds.sendCustomerEmails]: [permissionIds.customers],
  [permissionIds.billInvoiceSettings]: [permissionIds.customers],
  [permissionIds.suppliers]: [
    permissionIds.recordExpenses,
    permissionIds.billerManagement,
  ],
};

const LEGACY_UPLOAD_ENTRY_PERMISSION_ALIASES = [
  permissionIds.legacyJournals,
  permissionIds.legacyTransactionsReceipt,
] as const;

/** Retired permission id — stripped on save; migrated to upload_entry in DB. */
const RETIRED_MEMBER_PERMISSION_IDS = ['magic_entry'] as const;

export function memberHasUploadEntryPermission(
  permissionIdsList: string[],
): boolean {
  if (permissionIdsList.includes(permissionIds.uploadEntry)) {
    return true;
  }
  return LEGACY_UPLOAD_ENTRY_PERMISSION_ALIASES.some((legacyId) =>
    permissionIdsList.includes(legacyId),
  );
}

export function memberHasUploadEntryPageAccess(
  permissionIdsList: string[],
): boolean {
  return (
    memberHasUploadEntryPermission(permissionIdsList) ||
    permissionIdsList.includes(permissionIds.uploadEntryUploadOnly)
  );
}

export function memberHasUploadEntryUploadOnlyPermission(
  permissionIdsList: string[],
): boolean {
  if (memberHasUploadEntryPermission(permissionIdsList)) {
    return false;
  }
  return permissionIdsList.includes(permissionIds.uploadEntryUploadOnly);
}

const LEGACY_SEND_CUSTOMER_EMAILS_PERMISSION_ALIASES = [
  permissionIds.customers,
] as const;

export function memberHasSendCustomerEmailsPermission(
  permissionIdsList: string[],
): boolean {
  if (permissionIdsList.includes(permissionIds.sendCustomerEmails)) {
    return true;
  }
  return LEGACY_SEND_CUSTOMER_EMAILS_PERMISSION_ALIASES.some((legacyId) =>
    permissionIdsList.includes(legacyId),
  );
}

const LEGACY_BILL_INVOICE_SETTINGS_PERMISSION_ALIASES = [
  permissionIds.customers,
] as const;

export function memberHasBillInvoiceSettingsPermission(
  permissionIdsList: string[],
): boolean {
  if (permissionIdsList.includes(permissionIds.billInvoiceSettings)) {
    return true;
  }
  return LEGACY_BILL_INVOICE_SETTINGS_PERMISSION_ALIASES.some((legacyId) =>
    permissionIdsList.includes(legacyId),
  );
}

export function memberHasTaxCalendarPermission(
  permissionIdsList: string[],
): boolean {
  return permissionIdsList.includes(permissionIds.taxCalendar);
}

export function memberHasTaxCalendarPageAccess(
  permissionIdsList: string[],
): boolean {
  return (
    memberHasTaxCalendarPermission(permissionIdsList) ||
    permissionIdsList.includes(permissionIds.taxCalendarViewOnly)
  );
}

export function memberHasTaxCalendarViewOnlyPermission(
  permissionIdsList: string[],
): boolean {
  if (memberHasTaxCalendarPermission(permissionIdsList)) {
    return false;
  }
  return permissionIdsList.includes(permissionIds.taxCalendarViewOnly);
}

export function memberHasBusinessCalendarPermission(
  permissionIdsList: string[],
): boolean {
  return permissionIdsList.includes(permissionIds.businessCalendar);
}

const HRIS_PERMISSION_ALIASES: Record<string, readonly string[]> = {
  [permissionIds.hrisEmployees]: [permissionIds.hris],
  [permissionIds.hrisAttendance]: [permissionIds.hris],
  [permissionIds.hrisLeave]: [permissionIds.hris],
  [permissionIds.hrisPayroll]: [permissionIds.hris],
  [permissionIds.hrisSettings]: [permissionIds.hris],
  [permissionIds.hrisReports]: [permissionIds.hris],
};

/** Admin HRIS only — does not grant Employee Portal / legacy self-service. */
export function memberHasHrisPermission(
  permissionIdsList: string[],
  requiredId: string,
): boolean {
  if (requiredId === permissionIds.hrisSelfService) {
    return false;
  }
  if (permissionIdsList.includes(requiredId)) {
    return true;
  }
  if (
    requiredId !== permissionIds.hris &&
    permissionIdsList.includes(permissionIds.hris)
  ) {
    return true;
  }
  const aliases = HRIS_PERMISSION_ALIASES[requiredId] ?? [];
  return aliases.some((aliasId) => permissionIdsList.includes(aliasId));
}

/**
 * Employee Portal domain helper.
 * - `employee_portal` (All Access) satisfies any portal child.
 * - `employee_portal_attendance` implies base `/portal` when checking All Access / base.
 * - Legacy `hris_self_service` maps to All Access.
 */
export function memberHasEmployeePortalPermission(
  permissionIdsList: string[],
  requiredId: string,
): boolean {
  const hasLegacySelfService = permissionIdsList.includes(
    LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID,
  );
  const hasAllAccess =
    permissionIdsList.includes(permissionIds.employeePortal) ||
    hasLegacySelfService;
  const hasAttendance = permissionIdsList.includes(
    permissionIds.employeePortalAttendance,
  );

  if (requiredId === permissionIds.employeePortal) {
    return hasAllAccess || hasAttendance;
  }
  if (requiredId === permissionIds.employeePortalAttendance) {
    return hasAllAccess || hasAttendance;
  }
  return false;
}

export function memberHasEmployeePortalBaseAccess(
  permissionIdsList: string[],
): boolean {
  return memberHasEmployeePortalPermission(
    permissionIdsList,
    permissionIds.employeePortal,
  );
}

export function memberHasEmployeePortalAttendanceAccess(
  permissionIdsList: string[],
): boolean {
  return memberHasEmployeePortalPermission(
    permissionIdsList,
    permissionIds.employeePortalAttendance,
  );
}

export function memberHasJournalPermission(
  permissionIdsList: string[],
  journalPermissionId: JournalsModalPermissionId,
): boolean {
  if (permissionIdsList.includes(journalPermissionId)) {
    return true;
  }
  return LEGACY_JOURNAL_PERMISSION_ALIASES[journalPermissionId].some(
    (legacyId) => permissionIdsList.includes(legacyId),
  );
}

export function normalizePermissionsForModal(permissions: string[]): string[] {
  const withoutLegacy = permissions.filter(
    (id) =>
      !LEGACY_JOURNAL_PERMISSION_IDS.includes(
        id as (typeof LEGACY_JOURNAL_PERMISSION_IDS)[number],
      ),
  );
  const normalized = new Set(withoutLegacy);

  for (const ownerOnlyId of OWNER_ONLY_PERMISSION_IDS) {
    normalized.delete(ownerOnlyId);
  }

  for (const journalPermissionId of JOURNALS_MODAL_PERMISSION_IDS) {
    if (memberHasJournalPermission(permissions, journalPermissionId)) {
      normalized.add(journalPermissionId);
    }
  }

  if (permissions.includes(LEGACY_COMPANY_SETTINGS_PERMISSION_ID)) {
    normalized.delete(LEGACY_COMPANY_SETTINGS_PERMISSION_ID);
    for (const companySettingsId of COMPANY_SETTINGS_PERMISSION_IDS) {
      normalized.add(companySettingsId);
    }
  }

  if (memberHasUploadEntryPermission(permissions)) {
    normalized.add(permissionIds.uploadEntry);
    normalized.delete(permissionIds.uploadEntryUploadOnly);
  } else if (permissions.includes(permissionIds.uploadEntryUploadOnly)) {
    normalized.add(permissionIds.uploadEntryUploadOnly);
  }

  if (memberHasTaxCalendarPermission(permissions)) {
    normalized.add(permissionIds.taxCalendar);
    normalized.delete(permissionIds.taxCalendarViewOnly);
  } else if (permissions.includes(permissionIds.taxCalendarViewOnly)) {
    normalized.add(permissionIds.taxCalendarViewOnly);
  }

  if (permissions.includes(LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID)) {
    normalized.delete(LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID);
    normalized.add(permissionIds.employeePortal);
  }

  for (const retiredId of RETIRED_MEMBER_PERMISSION_IDS) {
    normalized.delete(retiredId);
  }

  return [...normalized];
}

export function sanitizePermissionsForSave(permissions: string[]): string[] {
  return permissions.filter(
    (id) =>
      !LEGACY_JOURNAL_PERMISSION_IDS.includes(
        id as (typeof LEGACY_JOURNAL_PERMISSION_IDS)[number],
      ) &&
      !OWNER_ONLY_PERMISSION_IDS.includes(
        id as (typeof OWNER_ONLY_PERMISSION_IDS)[number],
      ) &&
      !RETIRED_MEMBER_PERMISSION_IDS.includes(
        id as (typeof RETIRED_MEMBER_PERMISSION_IDS)[number],
      ) &&
      id !== LEGACY_COMPANY_SETTINGS_PERMISSION_ID &&
      id !== LEGACY_HRIS_SELF_SERVICE_PERMISSION_ID,
  );
}

export function memberHasTransactionsAreaAccess(
  permissionIdsList: string[],
): boolean {
  return memberHasJournalPermission(
    permissionIdsList,
    permissionIds.recordCustomTransactions,
  );
}

export function memberHasReportsHubAccess(
  permissionIdsList: string[],
): boolean {
  return memberHasAnyEndpointPermission(permissionIdsList, ['/reports']);
}

function normalizeMemberPath(path: string): string {
  return path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
}

/** `/portal` and legacy `/hris/me` — permission-gated even for company owners. */
export function isEmployeePortalScopedPath(path: string): boolean {
  const cleanPath = normalizeMemberPath(path);
  return (
    cleanPath === ROUTES.PORTAL.ROOT ||
    cleanPath.startsWith(`${ROUTES.PORTAL.ROOT}/`) ||
    cleanPath === ROUTES.HRIS.ME ||
    cleanPath.startsWith(`${ROUTES.HRIS.ME}/`)
  );
}

export function memberHasJournalsAreaAccess(
  permissionIdsList: string[],
): boolean {
  return JOURNALS_MODAL_PERMISSION_IDS.some((id) =>
    memberHasJournalPermission(permissionIdsList, id),
  );
}

export function memberCanAccessPath(
  permissionIdsList: string[],
  path: string,
): boolean {
  const cleanPath = normalizeMemberPath(path);

  if (
    cleanPath === ROUTES.SUPER_DASHBOARD_ACTIVITY_LOGS ||
    cleanPath.startsWith(`${ROUTES.SUPER_DASHBOARD_ACTIVITY_LOGS}/`)
  ) {
    return hasAppPermissionAccess(
      permissionIdsList,
      permissionIds.activityLogs,
    );
  }

  if (isUserLevelPath(cleanPath)) {
    return true;
  }

  if (isOwnerOnlyCompanyPath(cleanPath)) {
    return false;
  }

  if (
    cleanPath === ROUTES.JOURNALS.CUSTOMER_EMAIL ||
    cleanPath.startsWith(`${ROUTES.JOURNALS.CUSTOMER_EMAIL}/`)
  ) {
    return memberHasSendCustomerEmailsPermission(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.JOURNALS.BILL_INVOICE ||
    cleanPath.startsWith(`${ROUTES.JOURNALS.BILL_INVOICE}/`)
  ) {
    return memberHasBillInvoiceSettingsPermission(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.JOURNALS.CUSTOMERS ||
    cleanPath.startsWith(`${ROUTES.JOURNALS.CUSTOMERS}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.customers,
    );
  }

  if (
    cleanPath === ROUTES.JOURNALS.SUPPLIERS ||
    cleanPath.startsWith(`${ROUTES.JOURNALS.SUPPLIERS}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.suppliers,
    );
  }

  if (cleanPath === '/journals' || cleanPath.startsWith('/journals/')) {
    return memberHasJournalsAreaAccess(permissionIdsList);
  }

  if (cleanPath === '/reports' || cleanPath.startsWith('/reports/')) {
    if (cleanPath === '/reports') {
      return memberHasReportsHubAccess(permissionIdsList);
    }
    const pageId = getPagePermissionId(cleanPath);
    if (!pageId) {
      return false;
    }
    return hasAppPermissionAccess(permissionIdsList, pageId);
  }

  if (cleanPath === '/transactions' || cleanPath.startsWith('/transactions/')) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.recordCustomTransactions,
    );
  }

  if (
    cleanPath.startsWith('/sales') ||
    cleanPath.startsWith('/cash-receipts-journal')
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.recordIncome,
    );
  }

  if (
    cleanPath.includes('/issue-2307') ||
    cleanPath === ROUTES.QAP_1601EQ ||
    cleanPath.startsWith(`${ROUTES.QAP_1601EQ}/`)
  ) {
    return hasAppPermissionAccess(
      permissionIdsList,
      permissionIds.withholdingTaxReports,
    );
  }

  if (
    cleanPath.startsWith('/expenses') ||
    cleanPath.startsWith('/cash-disbursement-journal') ||
    cleanPath.startsWith('/cash-vouchers')
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.recordExpenses,
    );
  }

  if (
    cleanPath === ROUTES.BILLS_LEDGER.ROOT ||
    cleanPath.startsWith(`${ROUTES.BILLS_LEDGER.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.billerManagement,
    );
  }

  if (
    cleanPath === ROUTES.BILL_PAYMENTS.ROOT ||
    cleanPath.startsWith(`${ROUTES.BILL_PAYMENTS.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.billerManagement,
    );
  }

  if (
    cleanPath === ROUTES.FX_REVALUATIONS.ROOT ||
    cleanPath.startsWith(`${ROUTES.FX_REVALUATIONS.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.billerManagement,
    );
  }

  if (
    cleanPath === ROUTES.INVOICE_PAYMENTS.ROOT ||
    cleanPath.startsWith(`${ROUTES.INVOICE_PAYMENTS.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.arInvoices,
    );
  }

  if (
    cleanPath === ROUTES.AR_INVOICES.ROOT ||
    cleanPath.startsWith(`${ROUTES.AR_INVOICES.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.arInvoices,
    );
  }

  if (
    cleanPath === ROUTES.EQUIPMENT.ROOT ||
    cleanPath.startsWith(`${ROUTES.EQUIPMENT.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.equipmentManagement,
    );
  }

  if (
    cleanPath === ROUTES.EVENTS.ROOT ||
    cleanPath.startsWith(`${ROUTES.EVENTS.ROOT}/`)
  ) {
    return permissionIdsList.includes(permissionIds.eventsManagement);
  }

  if (
    cleanPath === ROUTES.RESERVE.ROOT ||
    cleanPath.startsWith(`${ROUTES.RESERVE.ROOT}/`)
  ) {
    return hasAppPermissionAccess(permissionIdsList, permissionIds.reserve);
  }

  if (
    cleanPath === ROUTES.PORTAL.ROOT ||
    cleanPath.startsWith(`${ROUTES.PORTAL.ROOT}/`)
  ) {
    if (
      cleanPath === ROUTES.PORTAL.ATTENDANCE ||
      cleanPath.startsWith(`${ROUTES.PORTAL.ATTENDANCE}/`)
    ) {
      return memberHasEmployeePortalAttendanceAccess(permissionIdsList);
    }
    return memberHasEmployeePortalBaseAccess(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.HRIS.ROOT ||
    cleanPath.startsWith(`${ROUTES.HRIS.ROOT}/`)
  ) {
    if (
      cleanPath === ROUTES.HRIS.ME ||
      cleanPath.startsWith(`${ROUTES.HRIS.ME}/`)
    ) {
      return memberHasEmployeePortalBaseAccess(permissionIdsList);
    }
    if (
      cleanPath === ROUTES.HRIS.EMPLOYEES ||
      cleanPath.startsWith(`${ROUTES.HRIS.EMPLOYEES}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisEmployees,
      );
    }
    if (
      cleanPath === ROUTES.HRIS.ATTENDANCE ||
      cleanPath.startsWith(`${ROUTES.HRIS.ATTENDANCE}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisAttendance,
      );
    }
    if (
      cleanPath === ROUTES.HRIS.LEAVE ||
      cleanPath.startsWith(`${ROUTES.HRIS.LEAVE}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisLeave,
      );
    }
    if (
      cleanPath === ROUTES.HRIS.PAYROLL ||
      cleanPath.startsWith(`${ROUTES.HRIS.PAYROLL}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisPayroll,
      );
    }
    if (
      cleanPath === ROUTES.HRIS.SETTINGS ||
      cleanPath.startsWith(`${ROUTES.HRIS.SETTINGS}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisSettings,
      );
    }
    if (
      cleanPath === ROUTES.HRIS.REPORTS ||
      cleanPath.startsWith(`${ROUTES.HRIS.REPORTS}/`)
    ) {
      return memberHasHrisPermission(
        permissionIdsList,
        permissionIds.hrisReports,
      );
    }
    if (cleanPath === ROUTES.HRIS.GUIDE) {
      return (
        memberHasHrisPermission(permissionIdsList, permissionIds.hris) ||
        memberHasEmployeePortalBaseAccess(permissionIdsList)
      );
    }
    return memberHasHrisPermission(permissionIdsList, permissionIds.hris);
  }

  if (
    cleanPath === ROUTES.CAPITALS.ROOT ||
    cleanPath.startsWith(`${ROUTES.CAPITALS.ROOT}/`)
  ) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.capitalManagement,
    );
  }

  if (
    cleanPath === ROUTES.UPLOAD_ENTRY_PROCESS ||
    cleanPath.startsWith(`${ROUTES.UPLOAD_ENTRY_PROCESS}?`) ||
    cleanPath.startsWith(`${ROUTES.UPLOAD_ENTRY_PROCESS}/`)
  ) {
    return memberHasUploadEntryPermission(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.UPLOAD_ENTRY ||
    cleanPath.startsWith(`${ROUTES.UPLOAD_ENTRY}/`)
  ) {
    return memberHasUploadEntryPageAccess(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.SHOP.ROOT ||
    cleanPath.startsWith(`${ROUTES.SHOP.ROOT}/`)
  ) {
    return hasAppPermissionAccess(permissionIdsList, permissionIds.shop);
  }

  // Shared Manage Products — accessible with POS, InStoreQR, or Shop permission.
  if (
    cleanPath === ROUTES.POS.PRODUCTS ||
    cleanPath.startsWith(`${ROUTES.POS.PRODUCTS}/`)
  ) {
    return (
      hasAppPermissionAccess(permissionIdsList, permissionIds.posSystem) ||
      hasAppPermissionAccess(permissionIdsList, permissionIds.instoreqr) ||
      hasAppPermissionAccess(permissionIdsList, permissionIds.shop)
    );
  }

  if (
    cleanPath === '/tax-compliance' ||
    cleanPath.startsWith('/tax-compliance/')
  ) {
    return memberHasTaxCalendarPageAccess(permissionIdsList);
  }

  if (
    cleanPath === '/business-calendar' ||
    cleanPath.startsWith('/business-calendar/')
  ) {
    return memberHasBusinessCalendarPermission(permissionIdsList);
  }

  if (cleanPath === '/settings/company-settings') {
    return memberHasCompanySettingsPageAccess(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.SETTINGS.COMPANY_DETAILS ||
    cleanPath.startsWith(`${ROUTES.SETTINGS.COMPANY_DETAILS}/`)
  ) {
    return memberHasCompanyInfoAccess(permissionIdsList);
  }

  if (
    cleanPath === ROUTES.HEALTH_CHECK ||
    cleanPath.startsWith(`${ROUTES.HEALTH_CHECK}/`)
  ) {
    return hasAppPermissionAccess(permissionIdsList, permissionIds.healthCheck);
  }

  const pageId = getPagePermissionId(cleanPath);
  if (!pageId) {
    return false;
  }
  if (pageId === permissionIds.categories) {
    return memberHasJournalPermission(
      permissionIdsList,
      permissionIds.categories,
    );
  }
  if (hasAppPermissionAccess(permissionIdsList, pageId)) {
    return true;
  }
  return false;
}

export {
  getCachedPermissions,
  getPermissionsStorageKey,
} from './memberPermissionsStorage';

export function hasAppPermissionAccess(
  userPermissions: string[],
  appPermissionId: string,
): boolean {
  return userPermissions.includes(appPermissionId);
}
